import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import { DraftCreatableWorkflow } from '../../../data/models/workflow.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { setDraftWorkflow } from '../../../data/monitoring.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as monitoringSlice from '../../../data/monitoring.slice';
import { useEffect } from 'react';
import SelectMenu from '../../forms/SelectMenu';
import isNil from 'lodash/isNil';

interface AddWorkFlowDialogProps {
  workQueueUUID: string;
  open: boolean;
  hideWorkQueueSelector?: boolean;
  canSave?: boolean;
  close: (didConfirm?: boolean, workflowDraft?: DraftCreatableWorkflow) => void;
  save?: (workQueueUUID: string) => void;
}

interface AddWorkFlowFormData {
  name: string;
  description: string;
  workQueue: string;
}

const PREFIX = 'AddWorkflowDialog';

const classes = {
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  descriptionInput: `${PREFIX}-descriptionInput`,
  actions: `${PREFIX}-actions`,
  workQueueSelect: `${PREFIX}-workQueueSelect`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.title}`]: {
    padding: 0,
    margin: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingTop: `${theme.spacing(2)} !important`,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: 0,
    width: 500,
  },
  [`& .${classes.descriptionInput}`]: {
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: theme.spacing(2),
    padding: 0,
  },
  [`& .${classes.workQueueSelect}`]: {
    marginBottom: theme.spacing(2),
  },
}));

export default function AddWorkFlowDialog({
  workQueueUUID,
  open,
  hideWorkQueueSelector = false,
  canSave = true,
  close,
  save,
}: AddWorkFlowDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const workflowDraft = useAppSelector(monitoringSlice.selectDraftWorkflow);
  const workQueues = useAppSelector(monitoringSlice.selectWorkQueues);

  const formHook = useForm<AddWorkFlowFormData>({
    defaultValues: {
      name: '',
      description: '',
      workQueue: '',
    },
  });

  useEffect(() => {
    if (workflowDraft) {
      formHook.reset({
        name: workflowDraft.name,
        description: workflowDraft.description,
        workQueue: workflowDraft.workQueueUUID,
      });
    } else {
      formHook.reset({
        name: '',
        description: '',
        workQueue: workQueueUUID ?? '',
      });
    }
  }, [workflowDraft]);

  useEffect(() => {
    if (workQueueUUID) {
      formHook.reset({
        workQueue: workQueueUUID,
      });
    }
  }, [workQueueUUID]);

  const closeDialog = (
    didConfirm?: boolean,
    draftWorkflow?: DraftCreatableWorkflow,
  ) => {
    formHook.reset();
    close(didConfirm, draftWorkflow);
  };

  const handleClose = () => {
    closeDialog(false);
  };

  const handleSave = (data: AddWorkFlowFormData) => {
    const draftWorkflow: DraftCreatableWorkflow = {
      name: data.name,
      workQueueUUID: data.workQueue,
    };
    if (data.description) {
      draftWorkflow.description = data.description;
    }
    dispatch(setDraftWorkflow(draftWorkflow));
    closeDialog(true, draftWorkflow);
    save?.(data.workQueue);
  };

  const selectableWorkQueues = workQueues.map((queue) => ({
    key: queue.name,
    value: queue.workQueueUUID,
  }));

  let showWorkQueueSelector = true;
  if (hideWorkQueueSelector || workflowDraft) {
    showWorkQueueSelector = false;
  }

  let positiveActionButtonLabel = '';
  if (workflowDraft) {
    positiveActionButtonLabel = canSave ? t('common.save') : t('common.ok');
  } else {
    positiveActionButtonLabel = t('common.add');
  }

  return (
    <RootDialog open={open}>
      <DialogTitle className={classes.title}>
        {workflowDraft
          ? t('workflows.editWorkflow')
          : t('workflows.addWorkflow')}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <TextInput
          name="name"
          label={t('workflows.name')}
          control={formHook.control}
          required
          rules={{ required: t('common.requiredField') }}
          errors={formHook.formState.errors}
        />
        <TextInput
          className={classes.descriptionInput}
          name="description"
          label={t('workflows.description')}
          control={formHook.control}
          errors={formHook.formState.errors}
          multiline
        />
        {showWorkQueueSelector && (
          <SelectMenu
            name="workQueue"
            label={t('workflows.workQueue')}
            className={classes.workQueueSelect}
            values={selectableWorkQueues}
            valueAttr="value"
            labelAttr="key"
            control={formHook.control}
            required
            errorMessage={
              formHook.formState.errors.workQueue?.message as string
            }
            rules={{ required: t('common.requiredField') }}
          />
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid}
          onClick={formHook.handleSubmit(handleSave)}>
          {positiveActionButtonLabel}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
